import { useState, useEffect, useRef, ChangeEvent, ReactNode } from 'react'
import { Box, IconButton, MenuItem, TextField, Typography, useTheme, Menu } from '@mui/material'
import { Icon } from '@iconify/react'
import { useTranslation } from 'react-i18next'

import { countriesWithCode, ICountry } from 'src/common/utils'
import { getCountryData } from 'src/common/utils/get-country-data.util'
import useStyles from './styles'

interface IPhoneInputProps {
  inputName: string
  handleChange: (value: string) => void
  value?: string | null
  error?: string
  inputPlaceholder: string
  inputUpperLabel: ReactNode
  fullWidth?: boolean
  disabled?: boolean
  required?: boolean
}

export const PhoneInput = ({
  inputName,
  handleChange,
  value,
  error,
  disabled,
  fullWidth,
  inputPlaceholder,
  inputUpperLabel,
  required,
}: IPhoneInputProps) => {
  const [t] = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const { countryWithCode, withoutCountryCode } = getCountryData(value || '')

  const [selectedCountry, setSelectedCountry] = useState<ICountry>(countryWithCode)
  const [phone, setPhone] = useState(withoutCountryCode || '')
  const [searchQuery, setSearchQuery] = useState<string>('')

  const textFieldRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const classes = useStyles({ fullWidth, disabled })
  const theme = useTheme()

  const filteredCountries = countriesWithCode.filter((country: ICountry) => {
    const searchString = `${country.name.toLowerCase()} ${country.code.toLowerCase()} ${
      country.phone
    }`
    return searchString.includes(searchQuery.toLowerCase())
  })

  const handleCountrySelect = (country: ICountry) => {
    setSelectedCountry(country)

    handleChange(`+${country.phone}${phone.replaceAll(' ', '')}`)
    setAnchorEl(null)
    setSearchQuery('')
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  useEffect(() => {
    if (phone.length > 0) {
      handleChange(`+${selectedCountry.phone}${phone.replaceAll(' ', '')}`)
    } else {
      handleChange('')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, selectedCountry])

  const handleRef = (ref: HTMLButtonElement | null): void => {
    // @ts-ignore
    textFieldRef.current = ref
  }

  const handleCloseFlagsMenu = (): void => {
    setAnchorEl(null)
  }

  return (
    <div className={classes.container}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            fontSize: '10px',
            color: theme.palette.custom.grey2,
            fontWeight: 600,
          }}
        >
          {inputUpperLabel}
        </Typography>
        <div className={`${classes.phoneInputWrapper}`}>
          <IconButton
            ref={handleRef}
            onClick={(event) => {
              event.preventDefault()

              setAnchorEl(textFieldRef.current)
            }}
            className={classes.codeInput}
          >
            <Icon height={18} icon={`flag:${selectedCountry.code.toLowerCase()}-4x3`} />
            <Typography sx={{ fontSize: '20px' }}>+{selectedCountry.phone}</Typography>
          </IconButton>

          <TextField
            className={classes.phoneInput}
            placeholder={inputPlaceholder}
            ref={inputRef}
            type="tel"
            name={inputName}
            value={phone}
            inputProps={{
              style: {
                width: '100%',
                fontSize: '20px',
                padding: 0,
              },
            }}
            // error={!!error}
            // helperText={error}
            onChange={(e) => {
              setPhone(e.target.value)
            }}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
          />
        </div>

        <Typography sx={{ fontSize: '12px', color: '#d32f2f', mt: '3px', height: '18px' }}>
          {(error && phone) || (required && error) ? t(error + '.v') : ''}
        </Typography>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        id="select-country"
        onClose={handleCloseFlagsMenu}
      >
        <TextField
          onKeyDown={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation()
            }
          }}
          value={searchQuery}
          onChange={handleSearchChange}
          variant={'standard'}
          sx={{ width: '100%', p: '6px 16px' }}
          placeholder={t('account-form-mobile-search-placeholder.v')}
          inputRef={searchInputRef}
          autoFocus
        />
        {filteredCountries.map((country) => {
          return (
            <MenuItem
              key={country.code}
              onClick={() => handleCountrySelect(country)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <Icon icon={`flag:${country.code.toLowerCase()}-4x3`} />
                {/* <span
                  className="iconify"
                  data-icon={`flag:${country.code.toLowerCase()}-4x3`}
                ></span> */}
                <Typography sx={{ fontSize: '16px' }}>{country.name}</Typography>
              </Box>
              <Typography sx={{ fontSize: '14px' }}>+{country.phone}</Typography>
            </MenuItem>
          )
        })}
      </Menu>
    </div>
  )
}
